// Public "accept invitation" page. The invited user lands here from the link in
// the branded email their organization sent (Phase 4). They set a password,
// which the BFF writes to Keycloak and activates the account. No login required
// — the one-time token in the URL authorizes the request.
import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { acceptInvitation } from "../usersRoles/api";

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await acceptInvitation(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message?.replace(/^API Error \d+:\s*/, "") || "Could not activate your account.");
    } finally {
      setBusy(false);
    }
  };

  const card = {
    p: 4, width: 420, maxWidth: "92vw", borderRadius: 3,
    boxShadow: "0 8px 30px rgba(15,23,42,0.12)",
  };
  const wrap = {
    minHeight: "100vh", display: "flex", alignItems: "center",
    justifyContent: "center", background: "#f1f4f9", p: 2,
  };

  if (!token) {
    return (
      <Box sx={wrap}>
        <Paper sx={card}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1B365D" }}>
            Invalid invitation link
          </Typography>
          <Typography sx={{ mt: 1, fontSize: "0.875rem", color: "#64748b" }}>
            This link is missing its token. Please use the link from your invitation
            email, or ask your administrator to resend it.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={wrap}>
        <Paper sx={{ ...card, textAlign: "center" }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#16a34a", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1B365D" }}>
            Account activated
          </Typography>
          <Typography sx={{ mt: 1, mb: 3, fontSize: "0.875rem", color: "#64748b" }}>
            Your password is set. You can now sign in to FederateX.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate("/")}>
            Go to sign in
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={wrap}>
      <Paper component="form" onSubmit={submit} sx={card}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#1B365D" }}>
          Set up your account
        </Typography>
        <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: "0.8125rem", color: "#64748b" }}>
          Choose a password to activate your FederateX account.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, fontSize: "0.8125rem" }}>{error}</Alert>}

        <TextField
          label="New password" type="password" fullWidth size="small" required
          value={password} onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }} autoComplete="new-password"
        />
        <TextField
          label="Confirm password" type="password" fullWidth size="small" required
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          sx={{ mb: 3 }} autoComplete="new-password"
        />
        <Button type="submit" variant="contained" fullWidth disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
          {busy ? "Activating…" : "Activate account"}
        </Button>
      </Paper>
    </Box>
  );
};

export default AcceptInvite;
